import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import { TILL_NUMBER, TILL_NAME } from '../utils/constants';
import { checkMpesaCodeExists, processPaymentAndUpdateFees } from '../services/firestore';
import { validateMpesaMessage } from '../utils/validators';
import useApplicationStore from '../stores/applicationStore';
import useAuthStore from '../stores/authStore';

const PaymentVerification = () => {
  const [mpesaMessage, setMpesaMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { selectedServiceFee, setCurrentApplication } = useApplicationStore();
  const { user, profile } = useAuthStore();

  useEffect(() => {
    // Redirect if no service fee selected or profile incomplete
    if (!selectedServiceFee || !profile?.profileCompleted) {
      navigate('/service-fee-selection');
    }
  }, [selectedServiceFee, profile, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate M-Pesa message using utility function
    const validation = validateMpesaMessage(mpesaMessage, selectedServiceFee.fee, TILL_NAME);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Check if M-Pesa code has been used before (Firebase cost optimization - direct lookup)
      const codeCheck = await checkMpesaCodeExists(validation.mpesaCode);
      
      if (!codeCheck.success) {
        setErrors({ message: 'Error checking M-Pesa code. Please try again.' });
        return;
      }

      if (codeCheck.exists) {
        setErrors({ message: 'This M-Pesa transaction has already been used' });
        return;
      }

      // Create loan application data
      const applicationData = {
        mpesaNumber: profile?.mpesaNumber,
        loanPurpose: profile?.loanPurpose || 'Personal',
        serviceAmount: selectedServiceFee.fee,
        eligibleLoanAmount: selectedServiceFee.loanAmount,
        status: 'pending'
      };

      console.log('Application data being submitted:', applicationData); // Debug log

      // Process payment atomically (creates application, stores M-Pesa code, updates service fees)
      const result = await processPaymentAndUpdateFees(
        validation.mpesaCode,
        user.uid,
        applicationData,
        selectedServiceFee.fee
      );

      console.log('Payment processed successfully:', result); // Debug log

      // Set current application in store
      const newApplication = {
        id: result.applicationId,
        ...applicationData,
        appliedAt: new Date().toISOString()
      };
      
      setCurrentApplication(newApplication);

      // Navigate to success page
      navigate('/application-status');

    } catch (error) {
      console.error('Verification error:', error);
      
      // Handle specific error messages
      if (error.message === 'M-Pesa code already used') {
        setErrors({ message: 'This M-Pesa transaction has already been used' });
      } else {
        setErrors({ message: 'An error occurred. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/payment-instructions');
  };

  const handleMessageChange = (e) => {
    setMpesaMessage(e.target.value);
    if (errors.message) {
      setErrors(prev => ({ ...prev, message: '' }));
    }
  };

  // Show loading if no service fee data
  if (!selectedServiceFee) {
    return (
      <div className="page-container">
        <div className="form-container">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Verify Payment
          </h1>
          <p className="text-gray-600">
            Paste your M-Pesa confirmation message below
          </p>
        </div>

        {/* Payment Summary */}
        <div className="bg-primary-50 rounded-xl p-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-primary-700 font-medium">Expected Payment:</span>
            <span className="font-bold text-primary-900">KES {selectedServiceFee.fee}</span>
          </div>
          <div className="flex justify-between items-center text-sm mt-1">
            <span className="text-primary-600">For loan amount:</span>
            <span className="font-medium text-primary-800">KES {selectedServiceFee.loanAmount.toLocaleString()}</span>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-blue-800 text-sm font-medium mb-1">
                Copy the entire M-Pesa confirmation message
              </p>
              {/*<p className="text-blue-700 text-sm">
                It should start with a transaction code and mention "{TILL_NAME}"
              </p>*/}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              M-Pesa Confirmation Message *
            </label>
            <textarea
              value={mpesaMessage}
              onChange={handleMessageChange}
              placeholder="Paste your M-Pesa confirmation message here..."
              rows={6}
              className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none ${
                errors.message ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.message && (
              <p className="mt-1 text-sm text-red-500">{errors.message}</p>
            )}
          </div>

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-red-700 text-sm">{errors.general}</span>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="flex-1"
              onClick={handleBack}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              type="submit"
              size="lg"
              className="flex-1"
              disabled={loading || !mpesaMessage.trim()}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </div>
              ) : (
                'Submit Application'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentVerification;